import { useCallback, useMemo } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';

export interface TableUrlStateParams {
  /** Base pathname (e.g. '/job-details') */
  pathname: string;
  /** Default page (0-based) */
  defaultPage?: number;
  /** Default rows per page */
  defaultRowsPerPage?: number;
  /** Query param keys */
  keys?: {
    page?: string;
    rpp?: string;
    [key: string]: string | undefined;
  };
}

export interface TableUrlStateResult {
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rpp: number) => void;
  /** Update URL with current or custom params. Merges with existing search. */
  syncToUrl: (params?: Record<string, string>) => void;
  /** Read a query param */
  getParam: (key: string) => string | null;
  /** Get all current search params as object */
  searchParams: Record<string, string>;
}

const DEFAULT_KEYS = { page: 'page', rpp: 'rpp' };

/**
 * Hook to keep table pagination (and optional filters) in URL.
 * Use with DrillDownTable so back navigation restores page/filters.
 */
export function useTableUrlState({
  pathname,
  defaultPage = 0,
  defaultRowsPerPage = 10,
  keys = DEFAULT_KEYS,
}: TableUrlStateParams): TableUrlStateResult {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const pageKey = keys.page ?? DEFAULT_KEYS.page!;
  const rppKey = keys.rpp ?? DEFAULT_KEYS.rpp!;

  const page = useMemo(() => {
    const p = searchParams.get(pageKey);
    const n = p ? parseInt(p, 10) : defaultPage;
    return Number.isFinite(n) && n >= 0 ? n : defaultPage;
  }, [searchParams, pageKey, defaultPage]);

  const rowsPerPage = useMemo(() => {
    const r = searchParams.get(rppKey);
    const n = r ? parseInt(r, 10) : defaultRowsPerPage;
    return Number.isFinite(n) && n > 0 ? n : defaultRowsPerPage;
  }, [searchParams, rppKey, defaultRowsPerPage]);

  const syncToUrl = useCallback(
    (params?: Record<string, string>) => {
      const current = Object.fromEntries(searchParams.entries());
      const next = { ...current, ...params };
      const q = new URLSearchParams(next);
      navigate({ pathname, search: q.toString() }, { replace: true });
    },
    [pathname, navigate, searchParams]
  );

  const setPage = useCallback(
    (p: number) => {
      syncToUrl({ [pageKey]: String(Math.max(0, p)) });
    },
    [syncToUrl, pageKey]
  );

  const setRowsPerPage = useCallback(
    (rpp: number) => {
      syncToUrl({ [rppKey]: String(Math.max(1, rpp)), [pageKey]: '0' });
    },
    [syncToUrl, rppKey, pageKey]
  );

  const getParam = useCallback(
    (key: string) => searchParams.get(key),
    [searchParams]
  );

  const searchParamsRecord = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  );

  return {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    syncToUrl,
    getParam,
    searchParams: searchParamsRecord,
  };
}
